import { useEffect, useMemo, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

const SPONSORS = [
  { slug: 'master', label: 'Master pitch' },
  { slug: 'the-licenced', label: 'The Licenced' },
  { slug: 'gold', label: 'Gold by Hemas' },
  { slug: 'pop-culture', label: 'Pop Culture' },
  { slug: 'camera-lk', label: 'Camera.lk' },
] as const

const PROFILE_TYPES = ['member', 'guest', 'sponsor', 'team'] as const

const ACCESS_PRESETS = [
  { key: 'member_viewer', label: 'Member rights', role: 'member', status: 'approved', profileType: 'member', bundleKey: 'sponsor_viewer', note: 'Approved external member, only granted pages.' },
  { key: 'sponsor_viewer', label: 'Sponsor rights', role: 'sponsor', status: 'approved', profileType: 'sponsor', bundleKey: 'sponsor_viewer', note: 'Approved sponsor, NDA-gated granted pages.' },
  { key: 'team_default', label: 'Team rights', role: 'team_member', status: 'approved', profileType: 'team', bundleKey: 'team_default', note: 'Internal team access.' },
  { key: 'admin_ops', label: 'Admin ops', role: 'admin', status: 'approved', profileType: 'team', bundleKey: 'admin_ops', note: 'Admin-only operational access.' },
  { key: 'guest_pending', label: 'Pending guest', role: 'guest', status: 'pending', profileType: 'guest', bundleKey: 'guest_pending', note: 'Pre-access holding state.' },
  { key: 'blocked', label: 'Blocked', role: 'viewer', status: 'blocked', profileType: 'guest', bundleKey: 'blocked', note: 'No application rights.' },
] as const

const ACCESS_PRESET_BY_KEY: Map<string, typeof ACCESS_PRESETS[number]> = new Map(ACCESS_PRESETS.map(preset => [preset.key, preset]))
const DOMAIN_ACCESS_PRESETS = ACCESS_PRESETS.filter(preset => ['member_viewer', 'sponsor_viewer', 'guest_pending', 'blocked'].includes(preset.key))
const SPONSOR_LABEL_BY_SLUG = new Map(SPONSORS.map(sponsor => [sponsor.slug, sponsor.label]))
const MEMBER_SPONSORS = SPONSORS.filter(sponsor => sponsor.slug !== 'master')
const DOMAIN_SPONSORS = MEMBER_SPONSORS
const COUNTRY_CODE_PUBLIC_SECOND_LEVELS = new Set([
  'ac',
  'assn',
  'co',
  'com',
  'edu',
  'gov',
  'grp',
  'hotel',
  'int',
  'ltd',
  'me',
  'net',
  'ngo',
  'nhs',
  'org',
  'plc',
  'police',
  'sch',
  'soc',
  'web',
])

const RIGHT_OPTIONS = [
  { key: 'admin.members.manage', label: 'Manage members' },
  { key: 'admin.content.manage', label: 'Manage content' },
  { key: 'admin.notifications.manage', label: 'Manage notifications' },
  { key: 'admin.integrations.manage', label: 'Manage integrations' },
  { key: 'pitch.view_all', label: 'View all pitches' },
  { key: 'pitch.view_granted', label: 'View granted pitches' },
  { key: 'pitch.preview_versions', label: 'Preview versions' },
  { key: 'pitch.analytics_references', label: 'View value-report references' },
  { key: 'pitch.bypass_nda', label: 'Bypass NDA' },
  { key: 'notification.acknowledge', label: 'Acknowledge todos' },
  { key: 'integrations.delivery.manage', label: 'Manage delivery queue' },
  { key: 'knockout.play', label: 'Play Knockout Session' },
  { key: 'admin.knockout.manage', label: 'Manage Knockout Session' },
] as const

const RIGHT_GROUPS = [
  { label: 'Admin', keys: ['admin.members.manage', 'admin.content.manage', 'admin.notifications.manage', 'admin.integrations.manage'] },
  { label: 'Pitch', keys: ['pitch.view_all', 'pitch.view_granted', 'pitch.preview_versions', 'pitch.analytics_references', 'pitch.bypass_nda'] },
  { label: 'Ops', keys: ['notification.acknowledge', 'integrations.delivery.manage'] },
  { label: 'Knockout', keys: ['knockout.play', 'admin.knockout.manage'] },
] as const

const BUNDLE_LOCK_NOTES: Record<string, string> = {
  founder_full: 'Locked founder record.',
  admin_ops: 'Admin-only defaults.',
  team_default: 'Team defaults; admin-management stays locked off.',
  sponsor_viewer: 'External member/sponsor defaults; granted pages only.',
  guest_pending: 'Pending users remain blocked from protected pages.',
  blocked: 'No access defaults.',
}

const EDITABLE_BUNDLES = new Set(['admin_ops', 'team_default', 'sponsor_viewer', 'guest_pending', 'blocked'])

interface MemberRecord {
  id: string
  email: string
  role: string
  status: string
  allowed_sponsors: string[] | null
  request_context: Record<string, unknown> | null
  display_name: string | null
  first_name: string | null
  last_name: string | null
  profile_image_url: string | null
  profile_image_path: string | null
  profile_type: string | null
  created_at: string
  updated_at: string
  approved_at: string | null
  founderLocked?: boolean
  rights?: MemberRights
}

interface MemberRights {
  bundleKey: string
  overrides: Record<string, boolean>
  effectiveRights: Record<string, boolean>
  notes?: string
  locked?: boolean
  missingTableFallback?: boolean
}

interface AccessBundle {
  key: string
  label: string
  description: string
  rights: Record<string, boolean>
}

interface PreapprovalRule {
  id: string
  email_domain: string
  role: string
  status: string
  allowed_sponsors: string[] | null
  profile_type: string | null
  access_bundle_key: string | null
  active: boolean
  notes: string | null
  updated_at: string
}

interface MembersResponse {
  members?: MemberRecord[]
  bundles?: AccessBundle[]
  preapprovalRules?: PreapprovalRule[]
  error?: string
}

interface AuthStartResponse {
  status?: string
  message?: string
  error?: string
  detail?: string
  setup?: {
    checklist?: string[]
  }
}

const AUTH_START_ERRORS: Record<string, string> = {
  auth_redirect_not_allowed: 'Supabase rejected the confirm redirect URL.',
  auth_email_rate_limited: 'Supabase rate-limited this email link request.',
  auth_email_provider_failed: 'Supabase could not hand the email to the configured mail provider.',
  auth_email_send_failed: 'Supabase could not start the email sign-in flow.',
  email_required: 'Email is required before a sign-in link can be sent.',
}

const EMAIL_DELIVERY_NOTE = 'Legacy Supabase fallback only. Primary sign-in is Continue with Yan on auth.yan.lk; central code emails can be rate-limited separately.'
const MEMBER_INVITE_NEXT = '/account'
const MEMBER_COLUMN_OPTIONS = [
  { key: 'dates', label: 'Dates' },
  { key: 'profile', label: 'Profile' },
  { key: 'access', label: 'Access' },
  { key: 'sponsors', label: 'Sponsors' },
  { key: 'rights', label: 'Advanced rights' },
  { key: 'actions', label: 'Actions' },
  { key: 'settings', label: 'Settings' },
] as const

type MemberColumnKey = typeof MEMBER_COLUMN_OPTIONS[number]['key']
type MemberColumns = Record<MemberColumnKey, boolean>
type MemberSortKey = 'updated_desc' | 'created_desc' | 'created_asc' | 'pending_first' | 'approved_first' | 'email_asc'

const DEFAULT_MEMBER_COLUMNS: MemberColumns = {
  dates: true,
  profile: true,
  access: true,
  sponsors: true,
  rights: true,
  actions: true,
  settings: true,
}

function memberSignInUrl(email: string) {
  const url = new URL('/signin', window.location.origin)
  url.searchParams.set('email', email)
  url.searchParams.set('next', MEMBER_INVITE_NEXT)
  return url.toString()
}

function CopyLinkIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <path d="M7.5 6.5h5a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-6a2 2 0 0 1-2-2v-5" />
      <path d="M7.5 3.5h-2a2 2 0 0 0-2 2v6" />
      <path d="M8.5 3.5h6a2 2 0 0 1 2 2v2" />
    </svg>
  )
}

function textFromContext(value: unknown) {
  return typeof value === 'string' ? value.trim() : ''
}

function formatDate(value: string | null) {
  if (!value) return 'Not yet'
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function selectedSponsors(formData: FormData) {
  return formData.getAll('allowedSponsors').map(value => String(value))
}

function selectedRights(formData: FormData, bundles: AccessBundle[]) {
  const bundleKey = String(formData.get('bundleKey') ?? 'guest_pending')
  const bundleRights = bundles.find(bundle => bundle.key === bundleKey)?.rights ?? {}
  const overrides: Record<string, boolean> = {}
  for (const item of RIGHT_OPTIONS) {
    const selected = formData.get(`right:${item.key}`) === 'on'
    if (selected !== (bundleRights[item.key] === true)) overrides[item.key] = selected
  }
  return {
    bundleKey,
    overrides,
    notes: String(formData.get('rightsNotes') ?? ''),
  }
}

function accessPresetFor(member: Pick<MemberRecord, 'role' | 'status' | 'profile_type' | 'rights'>) {
  if (member.status === 'blocked') return 'blocked'
  if (member.status === 'pending') return 'guest_pending'
  const bundleKey = member.rights?.bundleKey
  const exact = ACCESS_PRESETS.find(preset =>
    preset.role === member.role
    && preset.status === member.status
    && preset.profileType === (member.profile_type ?? preset.profileType)
    && (!bundleKey || preset.bundleKey === bundleKey)
  )
  if (exact) return exact.key
  if (member.role === 'admin') return 'admin_ops'
  if (member.role === 'team_member') return 'team_default'
  if (member.role === 'sponsor' || member.profile_type === 'sponsor') return 'sponsor_viewer'
  return 'member_viewer'
}

function accessPresetForRule(rule: PreapprovalRule) {
  const exact = DOMAIN_ACCESS_PRESETS.find(preset =>
    preset.role === rule.role
    && preset.status === rule.status
    && preset.profileType === (rule.profile_type ?? preset.profileType)
    && preset.bundleKey === (rule.access_bundle_key ?? preset.bundleKey)
  )
  if (exact) return exact.key
  if (rule.status === 'blocked') return 'blocked'
  if (rule.status === 'pending') return 'guest_pending'
  return rule.role === 'member' ? 'member_viewer' : 'sponsor_viewer'
}

function isUnsafeDomainRule(rule: PreapprovalRule) {
  const parts = rule.email_domain.split('.').filter(Boolean)
  const unsafeDomain = parts.length < 2 || (parts.length === 2 && parts[1].length === 2 && COUNTRY_CODE_PUBLIC_SECOND_LEVELS.has(parts[0]))
  const exact = DOMAIN_ACCESS_PRESETS.some(preset =>
    preset.role === rule.role
    && preset.status === rule.status
    && preset.profileType === (rule.profile_type ?? preset.profileType)
    && preset.bundleKey === (rule.access_bundle_key ?? preset.bundleKey)
  )
  return unsafeDomain || !exact || (rule.allowed_sponsors ?? []).includes('master')
}

function accessSummary(presetKey: string, fallback?: string) {
  const preset = ACCESS_PRESET_BY_KEY.get(presetKey)
  if (!preset) return fallback ?? 'Custom access'
  return [preset.label, preset.status, preset.profileType, fallback].filter(Boolean).join(' · ')
}

function bundleLabel(bundles: AccessBundle[], key?: string | null) {
  if (!key) return 'No bundle'
  return bundles.find(bundle => bundle.key === key)?.label ?? key.replaceAll('_', ' ')
}

function sponsorLabel(slug: string) {
  return SPONSOR_LABEL_BY_SLUG.get(slug as typeof SPONSORS[number]['slug']) ?? slug
}

function sponsorGrantSummary(slugs: string[]) {
  const sponsorSlugs = slugs.filter(slug => slug !== 'master')
  if (!sponsorSlugs.length) return 'No sponsor pages'
  if (sponsorSlugs.length === MEMBER_SPONSORS.length) return 'All sponsor pages'
  return sponsorSlugs.map(sponsorLabel).join(', ')
}

function overrideCount(rights?: MemberRights) {
  return Object.keys(rights?.overrides ?? {}).length
}

function emailDomain(email: string) {
  return email.split('@')[1]?.toLowerCase() ?? ''
}

function memberSearchText(member: MemberRecord, bundles: AccessBundle[]) {
  const allowedSponsors = (member.allowed_sponsors ?? []).filter(slug => slug !== 'master')
  const context = member.request_context ?? {}
  return [
    member.email,
    emailDomain(member.email),
    member.display_name,
    member.first_name,
    member.last_name,
    member.role,
    member.status,
    member.profile_type,
    accessSummary(accessPresetFor(member), bundleLabel(bundles, member.rights?.bundleKey)),
    sponsorGrantSummary(allowedSponsors),
    textFromContext(context.company),
    textFromContext(context.referral),
    textFromContext(context.questionnaire_url),
    member.rights?.notes,
  ].filter(Boolean).join(' ').toLowerCase()
}

function setNamedValue(form: HTMLFormElement | null, name: string, value: string) {
  if (!form) return
  const field = form.elements.namedItem(name)
  if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) field.value = value
}

function authStartErrorMessage(data: AuthStartResponse, fallback: string) {
  const base = data.error ? (AUTH_START_ERRORS[data.error] ?? data.error) : fallback
  const detail = data.detail && data.detail !== data.error ? ` Supabase detail: ${data.detail}` : ''
  const checklist = data.setup?.checklist?.length ? ' Check the admin auth setup checklist in the Functions README.' : ''
  return `${base}${detail}${checklist}`
}

function applyBundleDefaults(form: HTMLFormElement | null, bundle: AccessBundle | undefined) {
  if (!form || !bundle) return
  for (const item of RIGHT_OPTIONS) {
    const field = form.elements.namedItem(`right:${item.key}`)
    if (field instanceof HTMLInputElement) field.checked = bundle.rights[item.key] === true
  }
}

function applyAccessPreset(form: HTMLFormElement | null, presetKey: string, bundles: AccessBundle[]) {
  const preset = ACCESS_PRESET_BY_KEY.get(presetKey)
  if (!preset || !form) return
  setNamedValue(form, 'role', preset.role)
  setNamedValue(form, 'status', preset.status)
  setNamedValue(form, 'profileType', preset.profileType)
  setNamedValue(form, 'bundleKey', preset.bundleKey)
  const note = form.querySelector('[data-access-note]')
  if (note) note.textContent = preset.note
  applyBundleDefaults(form, bundles.find(bundle => bundle.key === preset.bundleKey))
}

function populateDomainRule(form: HTMLFormElement | null, rule: PreapprovalRule, bundles: AccessBundle[]) {
  if (!form) return
  const presetKey = accessPresetForRule(rule)
  const preset = ACCESS_PRESET_BY_KEY.get(presetKey) ?? ACCESS_PRESET_BY_KEY.get('sponsor_viewer')
  setNamedValue(form, 'emailDomain', rule.email_domain)
  setNamedValue(form, 'accessPreset', presetKey)
  setNamedValue(form, 'role', preset?.role ?? 'sponsor')
  setNamedValue(form, 'status', preset?.status ?? 'approved')
  setNamedValue(form, 'profileType', preset?.profileType ?? 'sponsor')
  setNamedValue(form, 'bundleKey', preset?.bundleKey ?? 'sponsor_viewer')
  setNamedValue(form, 'notes', rule.notes ?? '')
  const activeField = form.elements.namedItem('active')
  if (activeField instanceof HTMLInputElement) activeField.checked = rule.active
  const allowedSponsors = new Set(rule.allowed_sponsors ?? [])
  for (const field of Array.from(form.elements)) {
    if (field instanceof HTMLInputElement && field.name === 'allowedSponsors') {
      field.checked = field.value !== 'master' && allowedSponsors.has(field.value)
    }
  }
  applyBundleDefaults(form, bundles.find(bundle => bundle.key === preset?.bundleKey))
}

export default function AdminMembersPage() {
  const { isLoggedIn, loading, status, rights, email: currentEmail } = useAuth()
  const [members, setMembers] = useState<MemberRecord[]>([])
  const [bundles, setBundles] = useState<AccessBundle[]>([])
  const [preapprovalRules, setPreapprovalRules] = useState<PreapprovalRule[]>([])
  const [loadingMembers, setLoadingMembers] = useState(true)
  const [savingEmail, setSavingEmail] = useState<string | null>(null)
  const [savingRuleDomain, setSavingRuleDomain] = useState<string | null>(null)
  const [savingBundleKey, setSavingBundleKey] = useState<string | null>(null)
  const [sendingEmail, setSendingEmail] = useState<string | null>(null)
  const [copyingEmail, setCopyingEmail] = useState<string | null>(null)
  const [uploadingEmail, setUploadingEmail] = useState<string | null>(null)
  const [archivingEmail, setArchivingEmail] = useState<string | null>(null)
  const [memberSearch, setMemberSearch] = useState('')
  const [memberStatusFilter, setMemberStatusFilter] = useState('all')
  const [memberProfileFilter, setMemberProfileFilter] = useState('all')
  const [memberAccessFilter, setMemberAccessFilter] = useState('all')
  const [memberDomainFilter, setMemberDomainFilter] = useState('all')
  const [memberSponsorFilter, setMemberSponsorFilter] = useState('all')
  const [memberSort, setMemberSort] = useState<MemberSortKey>('updated_desc')
  const [memberDensity, setMemberDensity] = useState<'comfortable' | 'dense'>('dense')
  const [visibleMemberColumns, setVisibleMemberColumns] = useState<MemberColumns>(DEFAULT_MEMBER_COLUMNS)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const canAdmin = isLoggedIn && status === 'approved' && rights['admin.members.manage'] === true
  const memberDomains = useMemo(() => {
    return Array.from(new Set(members.map(member => emailDomain(member.email)).filter(Boolean))).sort()
  }, [members])
  const visibleMembers = useMemo(() => {
    const query = memberSearch.trim().toLowerCase()
    return members
      .filter(member => {
        const allowedSponsors = (member.allowed_sponsors ?? []).filter(slug => slug !== 'master')
        if (memberStatusFilter !== 'all' && member.status !== memberStatusFilter) return false
        if (memberProfileFilter !== 'all' && (member.profile_type ?? 'member') !== memberProfileFilter) return false
        if (memberAccessFilter !== 'all' && accessPresetFor(member) !== memberAccessFilter) return false
        if (memberDomainFilter !== 'all' && emailDomain(member.email) !== memberDomainFilter) return false
        if (memberSponsorFilter !== 'all' && !allowedSponsors.includes(memberSponsorFilter)) return false
        if (query && !memberSearchText(member, bundles).includes(query)) return false
        return true
      })
      .sort((a, b) => {
        if (memberSort === 'email_asc') return a.email.localeCompare(b.email)
        if (memberSort === 'created_asc') return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        if (memberSort === 'created_desc') return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        if (memberSort === 'pending_first') return Number(b.status === 'pending') - Number(a.status === 'pending') || a.email.localeCompare(b.email)
        if (memberSort === 'approved_first') return Number(b.status === 'approved') - Number(a.status === 'approved') || a.email.localeCompare(b.email)
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      })
  }, [bundles, memberAccessFilter, memberDomainFilter, memberProfileFilter, memberSearch, memberSort, memberSponsorFilter, memberStatusFilter, members])
  const hasMemberFilters = Boolean(
    memberSearch.trim()
    || memberStatusFilter !== 'all'
    || memberProfileFilter !== 'all'
    || memberAccessFilter !== 'all'
    || memberDomainFilter !== 'all'
    || memberSponsorFilter !== 'all'
    || memberSort !== 'updated_desc'
  )

  function upsertMember(nextMember: MemberRecord) {
    setMembers(prev => {
      const existingIndex = prev.findIndex(member => member.id === nextMember.id || member.email === nextMember.email)
      if (existingIndex === -1) return [nextMember, ...prev]
      return prev.map((member, index) => index === existingIndex ? nextMember : member)
    })
  }

  async function loadMembers(options: { quiet?: boolean } = {}) {
    if (!options.quiet) {
      setLoadingMembers(true)
      setError(null)
    }
    try {
      const res = await fetch('/api/admin/members', { credentials: 'include' })
      const data = await res.json() as MembersResponse
      if (!res.ok) throw new Error(data.error ?? 'Unable to load members')
      setMembers(data.members ?? [])
      setBundles(data.bundles ?? [])
      setPreapprovalRules(data.preapprovalRules ?? [])
    } catch (err) {
      if (!options.quiet) setError(err instanceof Error ? err.message : 'Unable to load members')
    } finally {
      if (!options.quiet) setLoadingMembers(false)
    }
  }

  useEffect(() => {
    if (!canAdmin) return undefined
    void loadMembers()
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void loadMembers({ quiet: true })
    }, 30 * 1000)
    return () => window.clearInterval(timer)
  }, [canAdmin])

  async function saveMember(payload: {
    email: string
    role: string
    status: string
    allowedSponsors: string[]
    displayName?: string
    firstName?: string
    lastName?: string
    profileType?: string
    questionnaireUrl?: string
    rights?: ReturnType<typeof selectedRights>
  }) {
    setSavingEmail(payload.email)
    setMessage(null)
    setError(null)
    try {
      const res = await fetch('/api/admin/members', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      })
      const data = await res.json() as { member?: MemberRecord; error?: string }
      if (!res.ok) throw new Error(data.error ?? 'Unable to save member')
      if (data.member) upsertMember(data.member)
      setMessage(`${payload.email} saved.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save member')
    } finally {
      setSavingEmail(null)
    }
  }

  async function saveBundleDefaults(e: React.FormEvent<HTMLFormElement>, bundle: AccessBundle) {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const rights: Record<string, boolean> = {}
    for (const item of RIGHT_OPTIONS) rights[item.key] = formData.get(`bundleRight:${item.key}`) === 'on'
    setSavingBundleKey(bundle.key)
    setMessage(null)
    setError(null)
    try {
      const res = await fetch('/api/admin/members', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'save_access_bundle',
          bundleKey: bundle.key,
          label: String(formData.get('label') ?? bundle.label),
          description: String(formData.get('description') ?? bundle.description),
          rights,
        }),
      })
      const data = await res.json() as { bundle?: AccessBundle; error?: string }
      if (!res.ok) throw new Error(data.error ?? 'Unable to save bundle defaults')
      if (data.bundle) setBundles(prev => prev.map(item => item.key === data.bundle?.key ? data.bundle as AccessBundle : item))
      await loadMembers({ quiet: true })
      setMessage(`${bundle.label} defaults saved.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save bundle defaults')
    } finally {
      setSavingBundleKey(null)
    }
  }

  function handleCreate(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const email = String(formData.get('email') ?? '').trim().toLowerCase()
    if (!email) return
    void saveMember({
      email,
      role: String(formData.get('role') ?? 'member'),
      status: String(formData.get('status') ?? 'approved'),
      allowedSponsors: selectedSponsors(formData),
      displayName: String(formData.get('displayName') ?? ''),
      firstName: String(formData.get('firstName') ?? ''),
      lastName: String(formData.get('lastName') ?? ''),
      profileType: String(formData.get('profileType') ?? 'member'),
      questionnaireUrl: String(formData.get('questionnaireUrl') ?? ''),
      rights: {
        bundleKey: String(formData.get('bundleKey') ?? 'sponsor_viewer'),
        overrides: {},
        notes: '',
      },
    })
    e.currentTarget.reset()
  }

  async function handleSaveDomainRule(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const emailDomain = String(formData.get('emailDomain') ?? '').trim().toLowerCase().replace(/^@+/, '')
    if (!emailDomain) return
    setSavingRuleDomain(emailDomain)
    setMessage(null)
    setError(null)
    try {
      const res = await fetch('/api/admin/members', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'save_preapproval_rule',
          emailDomain,
          role: String(formData.get('role') ?? 'sponsor'),
          status: String(formData.get('status') ?? 'approved'),
          allowedSponsors: selectedSponsors(formData),
          profileType: String(formData.get('profileType') ?? 'sponsor'),
          bundleKey: String(formData.get('bundleKey') ?? 'sponsor_viewer'),
          active: formData.get('active') === 'on',
          notes: String(formData.get('notes') ?? ''),
        }),
      })
      const data = await res.json() as { preapprovalRule?: PreapprovalRule; error?: string; detail?: string }
      if (!res.ok) throw new Error(data.detail ?? data.error ?? 'Unable to save domain rule')
      if (data.preapprovalRule) {
        setPreapprovalRules(prev => {
          const existingIndex = prev.findIndex(rule => rule.email_domain === data.preapprovalRule?.email_domain)
          if (existingIndex === -1) return [...prev, data.preapprovalRule as PreapprovalRule].sort((a, b) => a.email_domain.localeCompare(b.email_domain))
          return prev.map((rule, index) => index === existingIndex ? data.preapprovalRule as PreapprovalRule : rule)
        })
      }
      setMessage(`@${emailDomain} pre-approval saved.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save domain rule')
    } finally {
      setSavingRuleDomain(null)
    }
  }

  function handleUpdate(e: React.FormEvent<HTMLFormElement>, member: MemberRecord) {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const sponsorControlsRendered = formData.get('sponsorControlsRendered') === 'true'
    const rightsControlsRendered = formData.get('rightsControlsRendered') === 'true'
    void saveMember({
      email: member.email,
      role: String(formData.get('role') ?? member.role),
      status: String(formData.get('status') ?? member.status),
      allowedSponsors: sponsorControlsRendered ? selectedSponsors(formData) : member.allowed_sponsors ?? [],
      displayName: String(formData.get('displayName') ?? member.display_name ?? ''),
      firstName: String(formData.get('firstName') ?? member.first_name ?? ''),
      lastName: String(formData.get('lastName') ?? member.last_name ?? ''),
      profileType: String(formData.get('profileType') ?? member.profile_type ?? 'member'),
      questionnaireUrl: String(formData.get('questionnaireUrl') ?? textFromContext(member.request_context?.questionnaire_url)),
      rights: rightsControlsRendered
        ? selectedRights(formData, bundles)
        : member.rights ? {
          bundleKey: member.rights.bundleKey,
          overrides: member.rights.overrides ?? {},
          notes: member.rights.notes ?? '',
        } : undefined,
    })
  }

  async function copyMemberLink(member: MemberRecord) {
    const link = memberSignInUrl(member.email)
    setCopyingEmail(member.email)
    setMessage(null)
    setError(null)
    try {
      await navigator.clipboard.writeText(link)
      setMessage(`Copied safe profile sign-in link for ${member.email}.`)
    } catch {
      window.prompt('Copy this profile sign-in link:', link)
      setMessage(`Profile sign-in link ready for ${member.email}.`)
    } finally {
      setCopyingEmail(null)
    }
  }

  async function uploadProfileImage(email: string, file: File | null) {
    if (!file) return
    setUploadingEmail(email)
    setMessage(null)
    setError(null)
    try {
      const formData = new FormData()
      formData.set('email', email)
      formData.set('file', file)
      const res = await fetch('/api/admin/members/profile-image', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })
      const data = await res.json() as { member?: MemberRecord; url?: string; error?: string }
      if (!res.ok) throw new Error(data.error ?? 'Unable to upload profile image')
      if (data.member) upsertMember(data.member)
      setMessage(`Profile image saved for ${email}.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to upload profile image')
    } finally {
      setUploadingEmail(null)
    }
  }

  async function sendSignInLink(email: string) {
    setSendingEmail(email)
    setMessage(null)
    setError(null)
    try {
      const res = await fetch('/api/auth/start', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ provider: 'email', email, next: MEMBER_INVITE_NEXT, legacyEmailFallback: true }),
      })
      const data = await res.json() as AuthStartResponse
      if (!res.ok) throw new Error(authStartErrorMessage(data, 'Unable to send sign-in link'))
      setMessage(`${data.message ?? `Supabase accepted the legacy fallback link request for ${email}.`} ${EMAIL_DELIVERY_NOTE}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to send sign-in link')
    } finally {
      setSendingEmail(null)
    }
  }

  async function approveAndSendSignInLink(member: MemberRecord) {
    setSavingEmail(member.email)
    setSendingEmail(member.email)
    setMessage(null)
    setError(null)
    try {
      const saveRes = await fetch('/api/admin/members', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: member.email,
          role: member.role,
          status: 'approved',
          allowedSponsors: member.allowed_sponsors ?? [],
          rights: member.rights ? {
            bundleKey: member.rights.bundleKey,
            overrides: member.rights.overrides ?? {},
            notes: member.rights.notes ?? '',
          } : undefined,
        }),
      })
      const saveData = await saveRes.json() as { member?: MemberRecord; error?: string }
      if (!saveRes.ok) throw new Error(saveData.error ?? 'Unable to approve member')
      if (saveData.member) upsertMember(saveData.member)

      const linkRes = await fetch('/api/auth/start', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ provider: 'email', email: member.email, next: MEMBER_INVITE_NEXT, legacyEmailFallback: true }),
      })
      const linkData = await linkRes.json() as AuthStartResponse
      if (!linkRes.ok) throw new Error(authStartErrorMessage(linkData, 'Unable to send sign-in link'))

      setMessage(`Approved ${member.email}. ${linkData.message ?? 'Supabase accepted the legacy fallback link request.'} ${EMAIL_DELIVERY_NOTE}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to approve and send sign-in link')
    } finally {
      setSavingEmail(null)
      setSendingEmail(null)
    }
  }

  async function archiveMember(email: string) {
    const confirmed = window.confirm(`Archive ${email}? This hides the row, blocks access, and removes sponsor grants.`)
    if (!confirmed) return
    setArchivingEmail(email)
    setMessage(null)
    setError(null)
    try {
      const res = await fetch('/api/admin/members', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'archive', email }),
      })
      const data = await res.json() as { member?: MemberRecord; error?: string }
      if (!res.ok) throw new Error(data.error ?? 'Unable to archive member')
      setMembers(prev => prev.filter(member => member.email !== email))
      setMessage(`${email} archived.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to archive member')
    } finally {
      setArchivingEmail(null)
    }
  }

  if (loading) {
    return (
      <div className="admin-page">
        <p className="admin-muted">Checking admin access...</p>
      </div>
    )
  }

  if (!isLoggedIn) return <Navigate to="/signin" replace />

  if (!canAdmin) {
    return (
      <div className="admin-page">
        <span className="legal-eyebrow">Admin</span>
        <h1 className="admin-heading">Admin access required.</h1>
        <p className="admin-muted">This page is only available to approved Yan-Vada admins.</p>
        <Link to="/" className="btn btn-secondary">Back to pitch</Link>
      </div>
    )
  }

  const pendingCount = members.filter(member => member.status === 'pending').length
  const approvedCount = members.filter(member => member.status === 'approved').length
  const bundleStateKey = bundles.map(bundle => `${bundle.key}:${JSON.stringify(bundle.rights)}`).join('|')

  return (
    <div className="admin-page admin-members-page">
      <header className="admin-header">
        <div>
          <span className="legal-eyebrow">CMS · member access</span>
          <h1 className="admin-heading">Members</h1>
          <p className="admin-muted">
            Approve requests, pre-invite members, and grant sponsor pitch access.
          </p>
        </div>
        <div className="admin-stats" aria-label="Member counts">
          <div><strong>{members.length}</strong><span>Total</span></div>
          <div><strong>{pendingCount}</strong><span>Pending</span></div>
          <div><strong>{approvedCount}</strong><span>Approved</span></div>
        </div>
      </header>

      <form className="admin-card admin-create" onSubmit={handleCreate}>
        <div>
          <h2>Pre-approve or invite</h2>
          <p className="admin-muted">Add an email here first, then the person can sign in with Google, Apple, or a magic link.</p>
        </div>
        <label>
          <span>Email</span>
          <input name="email" type="email" placeholder="partner@company.com" required />
        </label>
        <label className="admin-access-preset">
          <span>Access = member rights</span>
          <select
            name="accessPreset"
            defaultValue="member_viewer"
            onChange={event => applyAccessPreset(event.currentTarget.form, event.currentTarget.value, bundles)}
          >
            {ACCESS_PRESETS.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}
          </select>
          <small data-access-note>{ACCESS_PRESET_BY_KEY.get('member_viewer')?.note}</small>
        </label>
        <input type="hidden" name="role" defaultValue="member" />
        <input type="hidden" name="status" defaultValue="approved" />
        <input type="hidden" name="profileType" defaultValue="member" />
        <input type="hidden" name="bundleKey" defaultValue="sponsor_viewer" />
        <button className="btn btn-primary" type="submit" disabled={savingEmail !== null}>
          {savingEmail ? 'Saving...' : 'Save member'}
        </button>
        <details className="admin-profile-panel admin-sponsor-panel">
          <summary>
            <span>Grants</span>
            <small>Optional pages</small>
          </summary>
          <fieldset className="admin-sponsor-fieldset admin-sponsor-chips">
            <legend>Sponsor access</legend>
            {MEMBER_SPONSORS.map(sponsor => (
              <label key={sponsor.slug} className="admin-checkbox">
                <input name="allowedSponsors" type="checkbox" value={sponsor.slug} />
                <span>{sponsor.label}</span>
              </label>
            ))}
          </fieldset>
        </details>
        <details className="admin-profile-panel admin-create-details">
          <summary>
            <span>Profile details</span>
            <small>Optional names and questionnaire</small>
          </summary>
          <div className="admin-create-detail-grid">
            <label>
              <span>Display name</span>
              <input name="displayName" type="text" placeholder="Member name" />
            </label>
            <label>
              <span>First name</span>
              <input name="firstName" type="text" placeholder="First" />
            </label>
            <label>
              <span>Last name</span>
              <input name="lastName" type="text" placeholder="K" />
            </label>
            <label>
              <span>Questionnaire URL</span>
              <input name="questionnaireUrl" type="url" placeholder="https://forms.gle/..." />
            </label>
          </div>
        </details>
      </form>

      <section className="admin-card admin-preapproval">
        <div>
          <h2>Domain pre-approval</h2>
          <p className="admin-muted">Auto-approve sponsor domains before they sign in through Yan. The Licenced remains NDA-gated, but the member row and sponsor grant are created automatically.</p>
        </div>
        <form id="domain-rule-form" className="admin-rule-form" onSubmit={handleSaveDomainRule}>
          <label>
            <span>Email domain or subdomain</span>
            <input name="emailDomain" type="text" defaultValue="thelicenced.com" placeholder="partner.thelicenced.com" required />
          </label>
          <label className="admin-access-preset">
            <span>Access = member rights</span>
            <select
              name="accessPreset"
              defaultValue="sponsor_viewer"
              onChange={event => applyAccessPreset(event.currentTarget.form, event.currentTarget.value, bundles)}
            >
              {DOMAIN_ACCESS_PRESETS.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
            <small data-access-note>{ACCESS_PRESET_BY_KEY.get('sponsor_viewer')?.note}</small>
          </label>
          <input type="hidden" name="role" defaultValue="sponsor" />
          <input type="hidden" name="status" defaultValue="approved" />
          <input type="hidden" name="profileType" defaultValue="sponsor" />
          <input type="hidden" name="bundleKey" defaultValue="sponsor_viewer" />
          <label className="admin-checkbox admin-rule-active">
            <input name="active" type="checkbox" defaultChecked />
            <span>Active</span>
          </label>
          <button className="btn btn-primary" type="submit" disabled={savingRuleDomain !== null}>
            {savingRuleDomain ? 'Saving...' : 'Save rule'}
          </button>
          <details className="admin-profile-panel admin-sponsor-panel">
            <summary>
              <span>Grants</span>
              <small>The Licenced</small>
            </summary>
            <fieldset className="admin-sponsor-fieldset admin-sponsor-chips">
              <legend>Sponsor access</legend>
              {DOMAIN_SPONSORS.map(sponsor => (
                <label key={sponsor.slug} className="admin-checkbox">
                  <input name="allowedSponsors" type="checkbox" value={sponsor.slug} defaultChecked={sponsor.slug === 'the-licenced'} />
                  <span>{sponsor.label}</span>
                </label>
              ))}
            </fieldset>
          </details>
          <details className="admin-profile-panel admin-rule-details">
            <summary>
              <span>Rule notes</span>
              <small>Optional internal context</small>
            </summary>
            <div className="admin-rule-detail-grid">
              <label>
                <span>Notes</span>
                <input name="notes" type="text" placeholder="Sponsor domain pre-approved for The Licenced" />
              </label>
            </div>
          </details>
        </form>
        <div className="admin-rule-list" aria-label="Pre-approval rules">
          {preapprovalRules.length === 0 ? (
            <p className="admin-muted">No stored domain rules yet. The app has a built-in fallback for @thelicenced.com until the migration is applied.</p>
          ) : preapprovalRules.map(rule => (
            <div key={rule.id ?? rule.email_domain} className="admin-rule-row">
              <div>
                <strong>@{rule.email_domain}</strong>
                <span>
                  {isUnsafeDomainRule(rule) ? 'invalid stored rule' : rule.active ? 'active' : 'inactive'}
                  {' · '}
                  {isUnsafeDomainRule(rule) ? 'resave to sanitize external-only access' : accessSummary(accessPresetForRule(rule))}
                  {' · '}
                  {(rule.allowed_sponsors ?? []).filter(slug => slug !== 'master').map(sponsorLabel).join(', ') || 'no sponsor grant'}
                </span>
              </div>
              <button
                className="btn btn-secondary btn-compact"
                type="button"
                onClick={() => populateDomainRule(document.getElementById('domain-rule-form') instanceof HTMLFormElement ? document.getElementById('domain-rule-form') as HTMLFormElement : null, rule, bundles)}
              >
                Use
              </button>
            </div>
          ))}
        </div>
      </section>

      <details className="admin-card admin-bundle-defaults">
        <summary>
          <span>Access bundle defaults</span>
          <small>Edit the rights inherited by member access labels.</small>
        </summary>
        <div className="admin-bundle-list">
          {bundles.map(bundle => {
            const editable = EDITABLE_BUNDLES.has(bundle.key)
            return (
              <form key={`${bundle.key}:${JSON.stringify(bundle.rights)}`} className="admin-bundle-row" onSubmit={(event) => saveBundleDefaults(event, bundle)}>
                <div className="admin-bundle-row-head">
                  <label>
                    <span>Label</span>
                    <input name="label" defaultValue={bundle.label} disabled={!editable} />
                  </label>
                  <label>
                    <span>Description</span>
                    <input name="description" defaultValue={bundle.description} disabled={!editable} />
                  </label>
                  <div className="admin-bundle-meta">
                    <strong>{bundle.key}</strong>
                    <small>{BUNDLE_LOCK_NOTES[bundle.key] ?? (editable ? 'Editable defaults.' : 'System locked.')}</small>
                  </div>
                  <button className="btn btn-primary" type="submit" disabled={!editable || savingBundleKey === bundle.key}>
                    {savingBundleKey === bundle.key ? 'Saving...' : editable ? 'Save defaults' : 'Locked'}
                  </button>
                </div>
                <div className="admin-bundle-rights" aria-label={`${bundle.label} default rights`}>
                  {RIGHT_GROUPS.map(group => (
                    <fieldset key={group.label}>
                      <legend>{group.label}</legend>
                      {group.keys.map(key => {
                        const item = RIGHT_OPTIONS.find(option => option.key === key)
                        if (!item) return null
                        return (
                          <label key={item.key} className="admin-checkbox">
                            <input
                              name={`bundleRight:${item.key}`}
                              type="checkbox"
                              defaultChecked={bundle.rights[item.key] === true}
                              disabled={!editable}
                            />
                            <span>{item.label}</span>
                          </label>
                        )
                      })}
                    </fieldset>
                  ))}
                </div>
              </form>
            )
          })}
        </div>
      </details>

      <section className="admin-card admin-member-toolbar" aria-label="Member CRM controls">
        <div className="admin-member-toolbar-head">
          <div>
            <strong>Member CRM</strong>
            <span>{visibleMembers.length} of {members.length} shown</span>
          </div>
          <div className="admin-member-density" role="group" aria-label="Row density">
            <button
              type="button"
              className={memberDensity === 'dense' ? 'active' : ''}
              onClick={() => setMemberDensity('dense')}
            >
              Dense
            </button>
            <button
              type="button"
              className={memberDensity === 'comfortable' ? 'active' : ''}
              onClick={() => setMemberDensity('comfortable')}
            >
              Full
            </button>
          </div>
        </div>
        <div className="admin-member-filters">
          <label className="admin-member-search">
            <span>Search</span>
            <input
              type="search"
              value={memberSearch}
              onChange={event => setMemberSearch(event.currentTarget.value)}
              placeholder="Email, name, company, sponsor..."
            />
          </label>
          <label>
            <span>Status</span>
            <select value={memberStatusFilter} onChange={event => setMemberStatusFilter(event.currentTarget.value)}>
              <option value="all">All statuses</option>
              {Array.from(new Set(members.map(member => member.status).filter(Boolean))).sort().map(item => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Profile</span>
            <select value={memberProfileFilter} onChange={event => setMemberProfileFilter(event.currentTarget.value)}>
              <option value="all">All profiles</option>
              {PROFILE_TYPES.map(item => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
          <label>
            <span>Access</span>
            <select value={memberAccessFilter} onChange={event => setMemberAccessFilter(event.currentTarget.value)}>
              <option value="all">All access</option>
              {ACCESS_PRESETS.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
          </label>
          <label>
            <span>Domain</span>
            <select value={memberDomainFilter} onChange={event => setMemberDomainFilter(event.currentTarget.value)}>
              <option value="all">All domains</option>
              {memberDomains.map(domain => <option key={domain} value={domain}>@{domain}</option>)}
            </select>
          </label>
          <label>
            <span>Sponsor</span>
            <select value={memberSponsorFilter} onChange={event => setMemberSponsorFilter(event.currentTarget.value)}>
              <option value="all">All sponsors</option>
              {MEMBER_SPONSORS.map(sponsor => <option key={sponsor.slug} value={sponsor.slug}>{sponsor.label}</option>)}
            </select>
          </label>
          <label>
            <span>Sort</span>
            <select value={memberSort} onChange={event => setMemberSort(event.currentTarget.value as MemberSortKey)}>
              <option value="updated_desc">Recently updated</option>
              <option value="created_desc">Newest created</option>
              <option value="created_asc">Oldest created</option>
              <option value="pending_first">Pending first</option>
              <option value="approved_first">Approved first</option>
              <option value="email_asc">Email A-Z</option>
            </select>
          </label>
          <details className="admin-member-column-menu">
            <summary>Columns</summary>
            <div>
              {MEMBER_COLUMN_OPTIONS.map(column => (
                <label key={column.key} className="admin-checkbox">
                  <input
                    type="checkbox"
                    checked={visibleMemberColumns[column.key]}
                    onChange={() => setVisibleMemberColumns(prev => ({ ...prev, [column.key]: !prev[column.key] }))}
                  />
                  <span>{column.label}</span>
                </label>
              ))}
            </div>
          </details>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={!hasMemberFilters}
            onClick={() => {
              setMemberSearch('')
              setMemberStatusFilter('all')
              setMemberProfileFilter('all')
              setMemberAccessFilter('all')
              setMemberDomainFilter('all')
              setMemberSponsorFilter('all')
              setMemberSort('updated_desc')
            }}
          >
            Clear
          </button>
        </div>
      </section>

      {(message || error) && (
        <div className={`admin-alert${error ? ' admin-alert-error' : ''}`} role={error ? 'alert' : 'status'}>
          {error ?? message}
        </div>
      )}

      <section className="admin-list" data-density={memberDensity} aria-label="Member list">
        {loadingMembers ? (
          <div className="admin-card"><p className="admin-muted">Loading members...</p></div>
        ) : members.length === 0 ? (
          <div className="admin-card"><p className="admin-muted">No members yet.</p></div>
        ) : visibleMembers.length === 0 ? (
          <div className="admin-card admin-members-empty">
            <p className="admin-muted">No members match the current filters.</p>
            <button
              type="button"
              className="btn btn-secondary btn-compact"
              onClick={() => {
                setMemberSearch('')
                setMemberStatusFilter('all')
                setMemberProfileFilter('all')
                setMemberAccessFilter('all')
                setMemberDomainFilter('all')
                setMemberSponsorFilter('all')
                setMemberSort('updated_desc')
              }}
            >
              Clear filters
            </button>
          </div>
        ) : visibleMembers.map(member => {
          const allowedSponsors = (member.allowed_sponsors ?? []).filter(slug => slug !== 'master')
          const context = member.request_context ?? {}
          const rights = member.rights
          const founderLocked = member.founderLocked || member.email === 'riz@dgtl.lk'
          const displayName = member.display_name || member.email
          return (
            <form key={`${member.id}:${member.updated_at}:${bundleStateKey}`} className="admin-card admin-member" onSubmit={(e) => handleUpdate(e, member)}>
              <div className="admin-member-main">
                <div className="admin-member-identity">
                  <div className="admin-member-avatar" aria-hidden="true">
                    {member.profile_image_url ? <img src={member.profile_image_url} alt="" /> : <span>{displayName.slice(0, 1).toUpperCase()}</span>}
                  </div>
                  <div>
                    <h2>{displayName}{founderLocked && <span className="admin-founder-lock">Founder locked</span>}</h2>
                    <p className="admin-muted admin-member-email">{member.email}</p>
                  </div>
                </div>
                {visibleMemberColumns.dates && (
                  <div className="admin-member-dates">
                    <p className="admin-muted">
                      Created {formatDate(member.created_at)} · Approved {formatDate(member.approved_at)}
                    </p>
                  </div>
                )}
                <span className={`admin-status admin-status-${member.status}`}>{founderLocked ? 'founder' : member.status}</span>
              </div>

              {(textFromContext(context.company) || textFromContext(context.referral)) && (
                <div className="admin-request-context">
                  {textFromContext(context.company) && <span>Company: {textFromContext(context.company)}</span>}
                  {textFromContext(context.referral) && <span>Referral: {textFromContext(context.referral)}</span>}
                </div>
              )}

              {visibleMemberColumns.profile && (
                <details className="admin-profile-panel">
                  <summary>
                    <span>Profile</span>
                    <small>{member.first_name || member.last_name ? `${member.first_name ?? ''} ${member.last_name ?? ''}`.trim() : 'Names, questionnaire, image'}</small>
                  </summary>
                  <div className="admin-member-profile">
                    <div className="admin-profile-fields">
                      <label>
                        <span>Display name</span>
                        <input name="displayName" type="text" defaultValue={member.display_name ?? ''} disabled={founderLocked} placeholder="Public/profile name" />
                      </label>
                      <label>
                        <span>First name</span>
                        <input name="firstName" type="text" defaultValue={member.first_name ?? ''} disabled={founderLocked} />
                      </label>
                      <label>
                        <span>Last name</span>
                        <input name="lastName" type="text" defaultValue={member.last_name ?? ''} disabled={founderLocked} />
                      </label>
                      <label>
                        <span>Questionnaire URL</span>
                        <input
                          name="questionnaireUrl"
                          type="url"
                          defaultValue={textFromContext(member.request_context?.questionnaire_url)}
                          disabled={founderLocked}
                          placeholder="https://forms.gle/..."
                        />
                      </label>
                    </div>
                    <label className="admin-profile-upload">
                      <span>Profile image</span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        disabled={founderLocked || uploadingEmail === member.email}
                        onChange={e => void uploadProfileImage(member.email, e.currentTarget.files?.[0] ?? null)}
                      />
                      <small>{uploadingEmail === member.email ? 'Uploading...' : 'JPG, PNG, or WebP up to 5 MB.'}</small>
                    </label>
                  </div>
                </details>
              )}

              {visibleMemberColumns.access ? (
                <div className="admin-member-controls">
                  <label className="admin-access-preset">
                    <span>Access = member rights</span>
                    <select
                      name="accessPreset"
                      defaultValue={founderLocked ? 'founder_full' : accessPresetFor(member)}
                      disabled={founderLocked}
                      onChange={event => applyAccessPreset(event.currentTarget.form, event.currentTarget.value, bundles)}
                    >
                      {founderLocked && <option value="founder_full">Founder full access</option>}
                      {ACCESS_PRESETS.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}
                    </select>
                    <small data-access-note>{founderLocked ? 'Locked founder record.' : accessSummary(accessPresetFor(member), bundleLabel(bundles, rights?.bundleKey))}</small>
                  </label>
                  <input type="hidden" name="role" defaultValue={member.role} />
                  <input type="hidden" name="status" defaultValue={member.status} />
                  <input type="hidden" name="profileType" defaultValue={PROFILE_TYPES.includes(member.profile_type as typeof PROFILE_TYPES[number]) ? member.profile_type ?? 'member' : 'member'} />
                </div>
              ) : (
                <>
                  <input type="hidden" name="role" defaultValue={member.role} />
                  <input type="hidden" name="status" defaultValue={member.status} />
                  <input type="hidden" name="profileType" defaultValue={PROFILE_TYPES.includes(member.profile_type as typeof PROFILE_TYPES[number]) ? member.profile_type ?? 'member' : 'member'} />
                </>
              )}

              {visibleMemberColumns.sponsors && (
                <details className="admin-profile-panel admin-sponsor-panel">
                  <summary>
                    <span>Sponsor grants</span>
                    <small>{sponsorGrantSummary(allowedSponsors)}</small>
                  </summary>
                  <input type="hidden" name="sponsorControlsRendered" value="true" />
                  <fieldset className="admin-sponsor-fieldset admin-sponsor-chips">
                    <legend>Sponsor access</legend>
                    {MEMBER_SPONSORS.map(sponsor => (
                      <label key={sponsor.slug} className="admin-checkbox">
                        <input
                          name="allowedSponsors"
                          type="checkbox"
                          value={sponsor.slug}
                          defaultChecked={allowedSponsors.includes(sponsor.slug)}
                          disabled={founderLocked}
                        />
                        <span>{sponsor.label}</span>
                      </label>
                    ))}
                  </fieldset>
                </details>
              )}

              {visibleMemberColumns.rights && (
                <details className="admin-rights-panel">
                  <summary>
                    <span>Advanced rights</span>
                    <small>{bundleLabel(bundles, rights?.bundleKey)} · {overrideCount(rights)} override{overrideCount(rights) === 1 ? '' : 's'}</small>
                  </summary>
                  <input type="hidden" name="rightsControlsRendered" value="true" />
                  <div className="admin-rights-content">
                    <label>
                      <span>Bundle</span>
                      <select
                        name="bundleKey"
                        defaultValue={rights?.bundleKey ?? 'guest_pending'}
                        disabled={founderLocked}
                        onChange={e => applyBundleDefaults(e.currentTarget.form, bundles.find(bundle => bundle.key === e.currentTarget.value))}
                      >
                        {bundles.map(bundle => <option key={bundle.key} value={bundle.key}>{bundle.label}</option>)}
                      </select>
                    </label>
                    <p className="admin-muted">Checkbox changes below save as manual overrides to the selected bundle.</p>
                    <div className="admin-rights-grid" aria-label="Effective rights">
                      {RIGHT_GROUPS.map(group => (
                        <div key={group.label} className="admin-rights-group">
                          <strong>{group.label}</strong>
                          {group.keys.map(key => {
                            const item = RIGHT_OPTIONS.find(option => option.key === key)
                            if (!item) return null
                            return (
                              <label key={item.key} className="admin-checkbox">
                                <input
                                  name={`right:${item.key}`}
                                  type="checkbox"
                                  defaultChecked={rights?.effectiveRights?.[item.key] === true}
                                  disabled={founderLocked}
                                />
                                <span>{item.label}</span>
                              </label>
                            )
                          })}
                        </div>
                      ))}
                    </div>
                    <label>
                      <span>Access notes</span>
                      <textarea name="rightsNotes" defaultValue={rights?.notes ?? ''} disabled={founderLocked} placeholder="Why this bundle or override exists." />
                    </label>
                    {rights?.missingTableFallback && <p className="admin-muted">Rights are using role defaults until migration 005 is applied.</p>}
                    {founderLocked && <p className="admin-muted">Founder access is locked to this email and cannot be changed here.</p>}
                  </div>
                </details>
              )}

              {visibleMemberColumns.actions && (
                <div className="admin-member-actions">
                  <button className="btn btn-secondary" type="submit" disabled={savingEmail === member.email || founderLocked}>
                    {founderLocked ? 'Founder locked' : (savingEmail === member.email ? 'Saving...' : 'Save changes')}
                  </button>
                  {member.status === 'pending' ? (
                    <button
                      className="btn btn-primary"
                      type="button"
                      disabled={savingEmail === member.email || sendingEmail === member.email || founderLocked}
                      onClick={() => void approveAndSendSignInLink(member)}
                    >
                      {sendingEmail === member.email ? 'Sending...' : 'Approve + fallback link'}
                    </button>
                  ) : (
                    <button
                      className="btn btn-primary"
                      type="button"
                      disabled={member.status !== 'approved' || sendingEmail === member.email}
                      onClick={() => void sendSignInLink(member.email)}
                    >
                      {sendingEmail === member.email ? 'Sending...' : 'Send fallback link'}
                    </button>
                  )}
                  <button
                    className="btn btn-secondary admin-copy-link-btn"
                    type="button"
                    disabled={copyingEmail === member.email}
                    onClick={() => void copyMemberLink(member)}
                  >
                    <CopyLinkIcon />
                    {copyingEmail === member.email ? 'Copying...' : 'Copy link'}
                  </button>
                </div>
              )}

              {visibleMemberColumns.settings && (
                <details className="admin-member-settings">
                  <summary>Settings</summary>
                  <div className="admin-member-settings-content">
                    <div>
                      <strong>Archive member</strong>
                      <p className="admin-muted">Hide this row, block access, and clear sponsor grants. Use only for cleanup or offboarding.</p>
                    </div>
                    <button
                      className="btn btn-danger"
                      type="button"
                      disabled={archivingEmail === member.email || member.email === currentEmail || founderLocked}
                      onClick={() => void archiveMember(member.email)}
                    >
                      {archivingEmail === member.email ? 'Archiving...' : 'Archive'}
                    </button>
                  </div>
                  {(member.email === currentEmail || founderLocked) && (
                    <p className="admin-muted admin-member-settings-note">
                      {founderLocked ? 'Founder records cannot be archived.' : 'You cannot archive your own active admin account.'}
                    </p>
                  )}
                </details>
              )}
            </form>
          )
        })}
      </section>
    </div>
  )
}
